// @ts-strict-ignore
import { FormInstance, useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import Spin from 'antd/lib/spin';
import { ErrorFallback } from './ErrorBoundary';
import { flatMap } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UpdateIntegrationRequestDataType } from '../actions/integrations';
import { selectOrgProviders } from '../selectors/selectors';
import { integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { formFieldsFromSettingFields } from '../helpers/settingFieldHelpers';
import { RootState } from '../reducers/reducers';
import { Integration, IntegrationInstallationId, IntegrationType } from '../types';
import { AppComponentProps, AppRouteComponentMatchParams } from '../typescriptHelpers';
import { additionalInfoNodeForProvider } from './IntegrationConfiguration';
import { globalMessage } from '../utils/globalMessage';
import { useIntegrationSettingFields } from '../utils/hooks/useIntegrationSettingFields';

interface EditIntegrationModalModalProps extends AppComponentProps {
    integration: Integration;
    open: boolean;
    isLoading: boolean;
    updateIntegration: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => void;
    onCancel: (integrationId: IntegrationInstallationId) => void;
}

const EditIntegrationModal: React.FC<EditIntegrationModalModalProps> = (props: EditIntegrationModalModalProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const { settingFields, isLoadingSettingFields, isSettingFieldsError } = useIntegrationSettingFields(
        props.orgId,
        props.appId,
        props.integration.installationId,
    );

    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration.providerId);

        return provider ?? null;
    });

    const onFinish = (
        integrationInstallationId: IntegrationInstallationId,
        integrationId: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                // BE-defined fields already use the save payload's shape
                if (!settingFields) {
                    throw Error('Invalid form values');
                }
                props.updateIntegration(integrationInstallationId, integrationId, values);
            })
            .catch((error) => {
                globalMessage.error(error.message);
                setFormIsValid(false);
            });
    };

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;

        setFormIsValid(isValid);
    };

    const fields = settingFields ? formFieldsFromSettingFields(settingFields, props.integration.id) : [];

    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onFinish,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
                props.integration.installationId,
            )
        :   null;

    return (
        <Modal
            title={`${props.integration.name} settings`}
            open={props.open}
            onCancel={() => props.onCancel(props.integration.installationId)}
            onOk={() => form.submit()}
            confirmLoading={props.isLoading}
            okButtonProps={{ disabled: !formIsValid }}
            okText="Update"
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
            destroyOnClose
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
            }}
        >
            {isSettingFieldsError ?
                <ErrorFallback />
            :   <div>
                    {isLoadingSettingFields ?
                        <Spin />
                    :   modalContent}
                    {additionalInfoForProvider && <div style={{ marginTop: 12 }}>{additionalInfoForProvider}</div>}
                </div>
            }
        </Modal>
    );
};

export default EditIntegrationModal;
